import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

/** @jsx jsx */
import { useCallback, useState } from 'react';
import { jsx } from '@emotion/core';
import Avatar from '@atlaskit/avatar';
import { PopupMenuGroup, Section } from '@atlaskit/menu';
import Popup from '@atlaskit/popup';
import { layers } from '@atlaskit/theme/constants';
import Tooltip from '@atlaskit/tooltip';
import AvatarGroupItem from './AvatarGroupItem';
import Grid from './Grid';
import MoreIndicator from './MoreIndicator';
import Stack from './Stack';
import { composeUniqueKey } from './utils';
var MAX_COUNT = {
  grid: 11,
  stack: 5
};

function getOverrides(overrides) {
  return {
    AvatarGroupItem: _objectSpread({
      render: function render(Component, props, index) {
        return jsx(Component, _extends({}, props, {
          key: composeUniqueKey(props.avatar, index)
        }));
      }
    }, overrides && overrides.AvatarGroupItem),
    Avatar: _objectSpread({
      render: function render(Component, props, index) {
        return jsx(Component, _extends({}, props, {
          key: composeUniqueKey(props, index)
        }));
      }
    }, overrides && overrides.Avatar)
  };
}

var AvatarGroup = function AvatarGroup(_ref) {
  var _ref$appearance = _ref.appearance,
      appearance = _ref$appearance === void 0 ? 'stack' : _ref$appearance,
      _ref$avatar = _ref.avatar,
      avatar = _ref$avatar === void 0 ? Avatar : _ref$avatar,
      borderColor = _ref.borderColor,
      boundariesElement = _ref.boundariesElement,
      data = _ref.data,
      isTooltipDisabled = _ref.isTooltipDisabled,
      maxCount = _ref.maxCount,
      onAvatarClick = _ref.onAvatarClick,
      onMoreClick = _ref.onMoreClick,
      overrides = _ref.overrides,
      _ref$showMoreButtonPr = _ref.showMoreButtonProps,
      showMoreButtonProps = _ref$showMoreButtonPr === void 0 ? {} : _ref$showMoreButtonPr,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? 'medium' : _ref$size,
      testId = _ref.testId;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isOpen = _useState2[0],
      setIsOpen = _useState2[1];

  var onClose = useCallback(function () {
    return setIsOpen(false);
  }, []);

  function renderMoreDropdown(max, total) {
    if (total <= max) {
      return null;
    }

    var renderMoreButton = function renderMoreButton(props) {
      return jsx(MoreIndicator, _extends({
        buttonProps: showMoreButtonProps,
        borderColor: borderColor,
        count: total - max,
        size: size,
        testId: testId && "".concat(testId, "--overflow-menu--trigger"),
        isActive: isOpen
      }, props));
    }; // bail if the consumer wants to handle onClick


    if (typeof onMoreClick === 'function') {
      return renderMoreButton({
        onClick: onMoreClick
      });
    } // split boundariesElement into `boundary` and `rootBoundary` props for Popup


    var boundary = boundariesElement === 'scrollParent' ? 'clippingParents' : undefined;

    var rootBoundary = function () {
      if (boundariesElement === 'scrollParent') {
        return undefined;
      }

      return boundariesElement === 'window' ? 'document' : 'viewport';
    }();

    return jsx(Popup, {
      isOpen: isOpen,
      onClose: onClose,
      placement: "bottom-end",
      boundary: boundary,
      rootBoundary: rootBoundary,
      shouldFlip: true,
      zIndex: layers.modal(),
      content: function content() {
        return jsx(PopupMenuGroup, {
          onClick: function onClick(e) {
            return e.stopPropagation();
          },
          minWidth: 250,
          maxHeight: 300
        }, jsx(Section, null, data.slice(max).map(function (avatar, index) {
          return getOverrides(overrides).AvatarGroupItem.render(AvatarGroupItem, {
            avatar: avatar,
            onAvatarClick: onAvatarClick,
            testId: testId && "".concat(testId, "--avatar-group-item-").concat(index + max),
            index: index + max
          }, // This index holds the true index,
          // adding up the index of non-overflowed avatars and overflowed avatars.
          index + max);
        })));
      },
      trigger: function trigger(triggerProps) {
        return renderMoreButton(_objectSpread(_objectSpread({}, triggerProps), {}, {
          onClick: function onClick() {
            return setIsOpen(!isOpen);
          }
        }));
      },
      testId: testId && "".concat(testId, "--overflow-menu")
    });
  }

  var max = maxCount === undefined || maxCount === 0 ? MAX_COUNT[appearance] : maxCount;
  var total = data.length;
  var maxAvatar = total > max ? max - 1 : max;
  var Group = appearance === 'stack' ? Stack : Grid;
  return jsx(Group, {
    testId: testId && "".concat(testId, "--avatar-group")
  }, data.slice(0, maxAvatar).map(function (avatarData, idx) {
    var callback = avatarData.onClick || onAvatarClick;
    var finalAvatar = getOverrides(overrides).Avatar.render(avatar, _objectSpread(_objectSpread({}, avatarData), {}, {
      size: size,
      borderColor: borderColor,
      testId: testId && "".concat(testId, "--avatar-").concat(idx),
      onClick: callback ? function (event, analyticsEvent) {
        callback(event, analyticsEvent, idx);
      } : undefined,
      stackIndex: max - idx
    }), idx);
    return !isTooltipDisabled ? jsx(Tooltip, {
      key: composeUniqueKey(avatarData, idx),
      content: avatarData.name
    }, finalAvatar) : finalAvatar;
  }), renderMoreDropdown(+maxAvatar, total));
};

export default AvatarGroup;